import React, { useEffect, useState } from "react";
import { TagsSelect, IconButton, TextEditor } from "@SeemplicityDev/shared";
import { fieldTypeOptions } from "../../../index.js";
import type { FieldType } from "../../GenericField/GenericField";
import "./TicketTemplatePreview.scss";

export interface TemplateField {
  key: string;
  label?: string;
  displayValue?: string;
  value?: unknown;
  isCompatible?: boolean;
  fieldType?: FieldType;
}

interface FieldProps {
  "data-testid": string;
  name: string;
  labelWidth: string;
  readOnly: boolean;
  noMargin: boolean;
  label?: string;
  wrapperClassName: string;
  disabled: boolean;
}

export interface TicketTemplatePreviewProps {
  fields?: TemplateField[];
  onDeleteField?: (key: string) => void;
  isDeletable?: boolean;
}

const TicketTemplatePreview: React.FC<TicketTemplatePreviewProps> = ({
  fields,
  onDeleteField = () => {},
  isDeletable = true,
}) => {
  const [compatibleFields, setCompatibleFields] = useState<TemplateField[]>([]);
  const [incompatibleFields, setIncompatibleFields] = useState<TemplateField[]>(
    []
  );

  useEffect(() => {
    let updatedCompatibleFields: TemplateField[] = [];
    let updatedIncompatibleFields: TemplateField[] = [];

    fields?.forEach((field) => {
      if (field.isCompatible === false) {
        updatedIncompatibleFields.push(field);
      } else {
        updatedCompatibleFields.push(field);
      }
    });

    setCompatibleFields(updatedCompatibleFields);
    setIncompatibleFields(updatedIncompatibleFields);
  }, [fields]);

  const getFieldProps = (field: TemplateField): FieldProps => {
    return {
      "data-testid": `field-template-${field.key}`,
      name: `field-template-${field.key}`,
      labelWidth: "134px",
      readOnly: true,
      noMargin: true,
      label: field.label,
      wrapperClassName: "input-wrap-template-seem-html",
      //if undefined this should be enabled
      disabled: field.isCompatible === false,
    };
  };

  const showFields = (fields: TemplateField[]) => {
    return fields?.map((field) => (
      <div className={`field`} key={field.key}>
        {field.fieldType === fieldTypeOptions.TAGS_LIST ? (
          <TagsSelect
            {...getFieldProps(field)}
            // `displayValue` is a preview string; TagsSelect's value expects
            // TagOption[]. Preserve the original runtime value via a cast.
            value={
              field.displayValue as unknown as React.ComponentProps<
                typeof TagsSelect
              >["value"]
            }
          />
        ) : (
          <TextEditor
            {...getFieldProps(field)}
            value={{ html: field.displayValue }}
            autoCompleteSettings={{
              // Read-only preview: no suggestions. Return [] to satisfy
              // GetAutocompleteOptions (was `() => {}` returning void).
              getAutocompleteOptions: () => [],
            }}
            hasBorder={false}
          />
        )}
        {!!isDeletable && (
          <IconButton
            wrapperClassName={`remove-field`}
            type="button"
            isIcon="seem-icon-bin"
            data-testid={`remove-field-${field.key}`}
            data-analytics-event="user_click"
            data-analytics-props={`{"name": "delete_field_btn", "container": "template_preview_modal"}`}
            onClick={() => {
              onDeleteField(field.key);
            }}
          />
        )}
      </div>
    ));
  };

  return (
    <div className={`template-fields ${isDeletable ? "is-deletable" : ""}`}>
      {showFields(compatibleFields)}
      {incompatibleFields.length > 0 && (
        <div>
          <div className={"incompatible-fields"}>Incompatible fields</div>
          {showFields(incompatibleFields)}
        </div>
      )}
    </div>
  );
};
export default TicketTemplatePreview;
